import { FunctionComponent, SVGProps, useMemo, useRef } from "react";

import { Box, Stack } from "@mui/material";
import { BackgroundThemeCategory, FormBackgroundTheme, resolveBackgroundThemeCategory } from "@simplyk/common";
import { Control, FieldPath, FieldValues, useController, useForm } from "react-hook-form";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorTab } from "@/components/LiveEditorElements/LiveEditorTab/LiveEditorTab";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import {
  Abstract,
  AbstractFilled,
  Aid,
  AidFilled,
  Animals,
  AnimalsFilled,
  Connectors,
  ConnectorsFilled,
  Environment,
  EnvironmentFilled,
  Fall,
  FallFilled,
  Football,
  FootballFilled,
  GivingTuesday,
  GivingTuesdayFilled,
  Pride,
  PrideFilled,
  Snow,
  SnowFilled,
  Religion,
  ReligionFilled,
  GameNight,
  GameNightFilled,
  Golf,
  GolfFilled,
  Health,
  HealthFilled,
  Hearts,
  HeartsFilled,
  Music,
  MusicFilled,
  School,
  SchoolFilled,
  Sparkles,
  SparklesFilled,
  Theatre,
  TheatreFilled,
} from "@/icons/special-themes";

type SvgIcon = FunctionComponent<SVGProps<SVGSVGElement>>;

interface ThemeOption {
  theme: FormBackgroundTheme;
  label: string;
  icon: SvgIcon;
  selectedIcon?: SvgIcon;
}

interface ThemeDefinition {
  theme: FormBackgroundTheme;
  icon: SvgIcon;
  selectedIcon?: SvgIcon;
}

const ANIMATED_THEME_DEFINITIONS: ThemeDefinition[] = [
  { theme: FormBackgroundTheme.Sparkles, icon: Sparkles, selectedIcon: SparklesFilled },
  { theme: FormBackgroundTheme.Hearts, icon: Hearts, selectedIcon: HeartsFilled },
  { theme: FormBackgroundTheme.Connectors, icon: Connectors, selectedIcon: ConnectorsFilled },
  { theme: FormBackgroundTheme.Music, icon: Music, selectedIcon: MusicFilled },
  { theme: FormBackgroundTheme.Cards, icon: GameNight, selectedIcon: GameNightFilled },
  { theme: FormBackgroundTheme.Faith, icon: Religion, selectedIcon: ReligionFilled },
  { theme: FormBackgroundTheme.Sports, icon: Football, selectedIcon: FootballFilled },
  { theme: FormBackgroundTheme.Fall, icon: Fall, selectedIcon: FallFilled },
  { theme: FormBackgroundTheme.Winter, icon: Snow, selectedIcon: SnowFilled },
  { theme: FormBackgroundTheme.GivingTuesday, icon: GivingTuesday, selectedIcon: GivingTuesdayFilled },
  { theme: FormBackgroundTheme.Pride, icon: Pride, selectedIcon: PrideFilled },
];

const STATIC_THEME_DEFINITIONS: ThemeDefinition[] = [
  { theme: FormBackgroundTheme.Abstract, icon: Abstract, selectedIcon: AbstractFilled },
  { theme: FormBackgroundTheme.Education, icon: School, selectedIcon: SchoolFilled },
  { theme: FormBackgroundTheme.Animal, icon: Animals, selectedIcon: AnimalsFilled },
  { theme: FormBackgroundTheme.Humanitarian, icon: Aid, selectedIcon: AidFilled },
  { theme: FormBackgroundTheme.CultureAndArts, icon: Theatre, selectedIcon: TheatreFilled },
  { theme: FormBackgroundTheme.Religion, icon: Religion, selectedIcon: ReligionFilled },
  { theme: FormBackgroundTheme.Environment, icon: Environment, selectedIcon: EnvironmentFilled },
  { theme: FormBackgroundTheme.Health, icon: Health, selectedIcon: HealthFilled },
  { theme: FormBackgroundTheme.Golf, icon: Golf, selectedIcon: GolfFilled },
];

const THEME_LABEL_MAP: Record<FormBackgroundTheme, string> = {
  [FormBackgroundTheme.Sparkles]: "liveFormEditor.stepColor.theme.sparkles",
  [FormBackgroundTheme.Hearts]: "liveFormEditor.stepColor.theme.hearts",
  [FormBackgroundTheme.Connectors]: "liveFormEditor.stepColor.theme.connectors",
  [FormBackgroundTheme.Music]: "liveFormEditor.stepColor.theme.music",
  [FormBackgroundTheme.Cards]: "liveFormEditor.stepColor.theme.gameNight",
  [FormBackgroundTheme.Faith]: "liveFormEditor.stepColor.theme.faith",
  [FormBackgroundTheme.Sports]: "liveFormEditor.stepColor.theme.sports",
  [FormBackgroundTheme.Fall]: "liveFormEditor.stepColor.theme.fall",
  [FormBackgroundTheme.Winter]: "liveFormEditor.stepColor.theme.winter",
  [FormBackgroundTheme.GivingTuesday]: "liveFormEditor.stepColor.theme.givingTuesday",
  [FormBackgroundTheme.Pride]: "liveFormEditor.stepColor.theme.pride",
  [FormBackgroundTheme.Abstract]: "liveFormEditor.stepColor.theme.abstract",
  [FormBackgroundTheme.Education]: "liveFormEditor.stepColor.theme.school",
  [FormBackgroundTheme.Animal]: "liveFormEditor.stepColor.theme.animals",
  [FormBackgroundTheme.Humanitarian]: "liveFormEditor.stepColor.theme.aid",
  [FormBackgroundTheme.CultureAndArts]: "liveFormEditor.stepColor.theme.theatre",
  [FormBackgroundTheme.Religion]: "liveFormEditor.stepColor.theme.faith",
  [FormBackgroundTheme.Environment]: "liveFormEditor.stepColor.theme.environment",
  [FormBackgroundTheme.Health]: "liveFormEditor.stepColor.theme.health",
  [FormBackgroundTheme.Golf]: "liveFormEditor.stepColor.theme.golf",
};

export interface BackgroundStyleSelectorProps<TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  onThemeChange?: (theme: FormBackgroundTheme | null, category: BackgroundThemeCategory) => void;
}

type CategoryFormData = {
  category: BackgroundThemeCategory;
};

export function BackgroundStyleSelector<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  onThemeChange,
}: BackgroundStyleSelectorProps<TFieldValues>): React.ReactElement {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const {
    field: { value, onChange },
  } = useController({ name, control });

  const backgroundTheme = value as FormBackgroundTheme | null;
  const category = resolveBackgroundThemeCategory(backgroundTheme);

  const lastThemeByCategoryRef = useRef<Partial<Record<BackgroundThemeCategory, FormBackgroundTheme>>>(
    backgroundTheme ? { [category]: backgroundTheme } : {}
  );

  const categoryForm = useForm<CategoryFormData>({ values: { category } });

  const staticShapesLabel = isSmallScreen
    ? t("dashboard", "liveFormEditor.stepColor.staticShapesMobile")
    : t("dashboard", "liveFormEditor.stepColor.staticShapes");

  const categoryTabs = useMemo(
    () => [
      { value: "simple" as BackgroundThemeCategory, label: t("dashboard", "liveFormEditor.stepColor.simple") },
      { value: "animated" as BackgroundThemeCategory, label: t("dashboard", "liveFormEditor.stepColor.animated") },
      { value: "static" as BackgroundThemeCategory, label: staticShapesLabel },
    ],
    [t, staticShapesLabel]
  );

  const themeOptions: ThemeOption[] = useMemo(() => {
    const definitions =
      category === "animated" ? ANIMATED_THEME_DEFINITIONS : category === "static" ? STATIC_THEME_DEFINITIONS : [];
    return definitions.map((definition) => ({
      ...definition,
      label: t("dashboard", THEME_LABEL_MAP[definition.theme] as Parameters<typeof t>[1]),
    }));
  }, [category, t]);

  const handleCategoryChange = (newCategory: BackgroundThemeCategory) => {
    let newTheme: FormBackgroundTheme | null = null;
    if (newCategory === "animated") {
      newTheme = lastThemeByCategoryRef.current.animated ?? ANIMATED_THEME_DEFINITIONS[0].theme;
    } else if (newCategory === "static") {
      newTheme = lastThemeByCategoryRef.current.static ?? STATIC_THEME_DEFINITIONS[0].theme;
    }
    onChange(newTheme);
    onThemeChange?.(newTheme, newCategory);
  };

  const handleThemeSelect = (theme: FormBackgroundTheme) => {
    lastThemeByCategoryRef.current[category] = theme;
    onChange(theme);
    onThemeChange?.(theme, category);
  };

  return (
    <LiveEditorBlock gap={1.5}>
      <DashboardTypography variant="subtitle1">
        {t("dashboard", "liveFormEditor.stepColor.selectBackgroundStyle")}
      </DashboardTypography>

      <LiveEditorTab<BackgroundThemeCategory, CategoryFormData>
        name="category"
        control={categoryForm.control}
        tabs={categoryTabs.map((tab) => ({
          ...tab,
          onChange: () => handleCategoryChange(tab.value),
        }))}
      />

      {themeOptions.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(5, 1fr)", lg: "repeat(6, 1fr)" },
            gap: 1,
            marginTop: 0.5,
          }}
        >
          {themeOptions.map((option) => {
            const isSelected = backgroundTheme === option.theme;
            const Icon = isSelected && option.selectedIcon ? option.selectedIcon : option.icon;
            return (
              <Stack
                key={option.theme}
                onClick={() => handleThemeSelect(option.theme)}
                sx={(theme) => ({
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  padding: 2,
                  borderRadius: theme.radius(1),
                  minWidth: 0,
                  overflow: "hidden",
                  border: `1px solid ${isSelected ? theme.palette.border.brand.intense : theme.palette.border.neutral.quiet}`,
                  backgroundColor: isSelected ? theme.palette.surface.neutral.white : "transparent",
                  color: isSelected ? theme.palette.text.brand.intense : theme.palette.text.neutral.quiet,
                  transition: "border-color 0.2s ease-in, background-color 0.2s ease-in, color 0.2s ease-in",
                  "&:hover": {
                    borderColor: isSelected
                      ? theme.palette.border.brand.intense
                      : theme.palette.border.neutral.moderate,
                  },
                })}
              >
                <Stack
                  sx={(theme) => ({
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    fill: "currentColor",
                    color: !isSelected ? theme.palette.text.neutral.supershy : "inherit",
                    "& > svg": { width: "100%", height: "100%" },
                  })}
                >
                  <Icon />
                </Stack>
                <DashboardTypography
                  variant="caption"
                  sx={{ color: "inherit", textAlign: "center", whiteSpace: "nowrap" }}
                >
                  {option.label}
                </DashboardTypography>
              </Stack>
            );
          })}
        </Box>
      )}
    </LiveEditorBlock>
  );
}
